/**
 * Wrappers Chart.js para gráficos del panel Trading.
 * Carga Chart.js si no está disponible (defer del CDN).
 */
import { h } from './_common.js';

async function ensureChart() {
  if (typeof window.Chart !== 'undefined') return window.Chart;
  // Esperar a que cargue el script (defer)
  for (let i = 0; i < 50; i++) {
    if (typeof window.Chart !== 'undefined') return window.Chart;
    await new Promise((r) => setTimeout(r, 100));
  }
  throw new Error('Chart.js no se cargó');
}

const DEFAULTS = {
  font: { family: 'Inter, system-ui, sans-serif', size: 11 },
  gridColor: 'rgba(255,255,255,0.04)',
  tickColor: '#9A9384',
  goldColor: '#EAB546',
  emeraldColor: '#10b981',
  redColor: '#ef4444',
};

function baseOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(20,18,16,0.95)',
        titleColor: '#F4F2EE',
        bodyColor: '#C8C2B5',
        borderColor: 'rgba(234,181,70,0.4)',
        borderWidth: 1,
        padding: 8,
        cornerRadius: 6,
      },
    },
    scales: {
      x: { grid: { color: DEFAULTS.gridColor, drawBorder: false }, ticks: { color: DEFAULTS.tickColor, font: DEFAULTS.font } },
      y: { grid: { color: DEFAULTS.gridColor, drawBorder: false }, ticks: { color: DEFAULTS.tickColor, font: DEFAULTS.font } },
    },
  };
}

/**
 * Equity curve chart con shaded drawdown.
 */
export async function renderEquityCurveChart(container, snapshots) {
  const Chart = await ensureChart();
  container.innerHTML = '';
  if (!snapshots || snapshots.length === 0) {
    container.appendChild(h('div', { class: 'text-center text-ink-400 py-8' }, 'Sin datos de equity todavía. El bot necesita primer tick.'));
    return null;
  }

  const labels = snapshots.map((s) => new Date(s.createdAt || s.time).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }));
  const equities = snapshots.map((s) => s.equity);
  const isUp = equities[equities.length - 1] >= equities[0];
  const lineColor = isUp ? DEFAULTS.emeraldColor : DEFAULTS.redColor;

  const canvas = h('canvas', { style: 'max-height: 280px;' });
  container.appendChild(canvas);

  return new Chart(canvas, {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: 'Equity',
        data: equities,
        borderColor: lineColor,
        backgroundColor: lineColor + '20',
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
      }],
    },
    options: {
      ...baseOptions(),
      plugins: {
        ...baseOptions().plugins,
        tooltip: {
          ...baseOptions().plugins.tooltip,
          callbacks: {
            label: (ctx) => `€${ctx.parsed.y.toFixed(2)}`,
          },
        },
      },
    },
  });
}

/**
 * Distribución P&L por mes (bars).
 */
export async function renderMonthlyPnlChart(container, closedTrades) {
  const Chart = await ensureChart();
  container.innerHTML = '';
  if (!closedTrades || closedTrades.length === 0) {
    container.appendChild(h('div', { class: 'text-center text-ink-400 py-8' }, 'Sin trades cerrados todavía.'));
    return null;
  }
  // Agrupar por YYYY-MM
  const byMonth = new Map();
  for (const t of closedTrades) {
    if (!t.closedAt) continue;
    const key = new Date(t.closedAt).toISOString().slice(0, 7);
    byMonth.set(key, (byMonth.get(key) || 0) + (t.realizedPnl || 0));
  }
  const months = Array.from(byMonth.keys()).sort();
  const values = months.map((m) => byMonth.get(m));
  const colors = values.map((v) => (v >= 0 ? DEFAULTS.emeraldColor : DEFAULTS.redColor));

  const canvas = h('canvas', { style: 'max-height: 220px;' });
  container.appendChild(canvas);

  return new Chart(canvas, {
    type: 'bar',
    data: {
      labels: months,
      datasets: [{
        label: 'P&L mensual',
        data: values,
        backgroundColor: colors,
        borderRadius: 4,
      }],
    },
    options: {
      ...baseOptions(),
      plugins: {
        ...baseOptions().plugins,
        tooltip: {
          ...baseOptions().plugins.tooltip,
          callbacks: { label: (ctx) => `€${ctx.parsed.y.toFixed(2)}` },
        },
      },
    },
  });
}

/**
 * Winrate por símbolo doughnut.
 */
export async function renderSymbolWinrateChart(container, bySymbol) {
  const Chart = await ensureChart();
  container.innerHTML = '';
  if (!bySymbol || bySymbol.length === 0) {
    container.appendChild(h('div', { class: 'text-center text-ink-400 py-8' }, 'Sin datos por símbolo aún.'));
    return null;
  }

  const labels = bySymbol.map((s) => s.symbol);
  const values = bySymbol.map((s) => s.pnl);
  const palette = ['#EAB546', '#10b981', '#ef4444', '#3b82f6', '#a78bfa', '#f59e0b', '#06b6d4'];

  const canvas = h('canvas', { style: 'max-height: 240px;' });
  container.appendChild(canvas);

  return new Chart(canvas, {
    type: 'doughnut',
    data: {
      labels,
      datasets: [{
        data: values.map(Math.abs),
        backgroundColor: labels.map((_, i) => palette[i % palette.length]),
        borderColor: '#0C0B09',
        borderWidth: 2,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'right', labels: { color: '#C8C2B5', font: DEFAULTS.font } },
        tooltip: {
          backgroundColor: 'rgba(20,18,16,0.95)',
          callbacks: {
            label: (ctx) => `${ctx.label}: €${values[ctx.dataIndex].toFixed(2)}`,
          },
        },
      },
    },
  });
}

/**
 * Drawdown area chart.
 */
export async function renderDrawdownChart(container, snapshots) {
  const Chart = await ensureChart();
  container.innerHTML = '';
  if (!snapshots || snapshots.length === 0) {
    container.appendChild(h('div', { class: 'text-center text-ink-400 py-8' }, 'Sin snapshots.'));
    return null;
  }

  const labels = snapshots.map((s) => new Date(s.createdAt || s.time).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }));
  const dd = snapshots.map((s) => -(s.drawdownPct || 0));

  const canvas = h('canvas', { style: 'max-height: 200px;' });
  container.appendChild(canvas);

  return new Chart(canvas, {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: 'Drawdown %',
        data: dd,
        borderColor: DEFAULTS.redColor,
        backgroundColor: DEFAULTS.redColor + '30',
        fill: true,
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 1.5,
      }],
    },
    options: {
      ...baseOptions(),
      plugins: {
        ...baseOptions().plugins,
        tooltip: {
          ...baseOptions().plugins.tooltip,
          callbacks: { label: (ctx) => `${ctx.parsed.y.toFixed(2)}%` },
        },
      },
    },
  });
}

/**
 * Backtest: equity curve + markers de trades.
 */
export async function renderBacktestEquityChart(container, equityCurve, trades) {
  const Chart = await ensureChart();
  container.innerHTML = '';
  if (!equityCurve || equityCurve.length === 0) {
    container.appendChild(h('div', { class: 'text-center text-ink-400 py-8' }, 'Sin datos de backtest.'));
    return null;
  }

  const labels = equityCurve.map((p) => new Date(p.time).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }));
  const equities = equityCurve.map((p) => p.equity);

  // Markers: convert trades into scatter datasets
  const longEntries = (trades || []).filter((t) => t.side === 'long').map((t) => ({ x: new Date(t.openTime).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }), y: t.entry }));
  const shortEntries = (trades || []).filter((t) => t.side === 'short').map((t) => ({ x: new Date(t.openTime).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }), y: t.entry }));

  const canvas = h('canvas', { style: 'max-height: 300px;' });
  container.appendChild(canvas);

  return new Chart(canvas, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Equity',
          data: equities,
          borderColor: DEFAULTS.goldColor,
          backgroundColor: DEFAULTS.goldColor + '15',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    },
    options: {
      ...baseOptions(),
      plugins: {
        ...baseOptions().plugins,
        tooltip: {
          ...baseOptions().plugins.tooltip,
          callbacks: { label: (ctx) => `€${ctx.parsed.y.toFixed(2)}` },
        },
      },
    },
  });
}
